import React, { useState, useEffect } from "react";
import { adminLogin, adminMe } from "../services/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import { Lock, LogOut, Loader2, Package, FileText, Settings2, Inbox, MessageSquare, LayoutTemplate, FolderTree, FileStack } from "lucide-react";
import logo from "../assets/logo.jpeg";
import AdminProducts from "../components/admin/AdminProducts";
import AdminBlog from "../components/admin/AdminBlog";
import AdminSettings from "../components/admin/AdminSettings";
import AdminQuotes from "../components/admin/AdminQuotes";
import AdminChatbot from "../components/admin/AdminChatbot";
import AdminContent from "../components/admin/AdminContent";
import AdminCategories from "../components/admin/AdminCategories";
import AdminPages from "../components/admin/AdminPages";

const Admin = () => {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const token = localStorage.getItem("bfm_admin_token");
    if (token) {
      adminMe().then(() => setAuthed(true)).catch(() => localStorage.removeItem("bfm_admin_token")).finally(() => setChecking(false));
    } else {
      setChecking(false);
    }
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await adminLogin(username, password);
      localStorage.setItem("bfm_admin_token", res.token);
      setAuthed(true);
      toast({ title: "Welcome back!" });
    } catch (err) {
      toast({ title: "Login failed", description: "Invalid username or password.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("bfm_admin_token");
    setAuthed(false);
    setUsername(""); setPassword("");
  };

  if (checking) {
    return <div className="min-h-screen flex items-center justify-center bg-neutral-50"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>;
  }

  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ink-gradient px-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex flex-col items-center mb-6">
            <img src={logo} alt="logo" className="h-16 w-16 object-contain" />
            <h1 className="font-display font-extrabold text-xl text-[#14171c] mt-3">Admin Dashboard</h1>
            <p className="text-sm text-neutral-500">Best For Medals HK Limited</p>
          </div>
          <form onSubmit={login} className="space-y-4">
            <div><Label>Username</Label><Input className="mt-1.5" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" /></div>
            <div><Label>Password</Label><Input type="password" className="mt-1.5" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></div>
            <Button type="submit" disabled={loading} className="w-full bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-lg h-11">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Sign In</>}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-[#14171c] text-white">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logo} alt="logo" className="h-9 w-9 object-contain bg-white rounded p-0.5" />
            <span className="font-display font-bold">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" rel="noreferrer" className="text-sm text-neutral-300 hover:text-white">View Site ↗</a>
            <button onClick={logout} className="inline-flex items-center gap-1.5 text-sm text-neutral-300 hover:text-white"><LogOut className="w-4 h-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Tabs defaultValue="products">
          <TabsList className="mb-6 bg-white border border-neutral-200 p-1 h-auto flex-wrap">
            <TabsTrigger value="products" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><Package className="w-4 h-4" /> Products</TabsTrigger>
            <TabsTrigger value="categories" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><FolderTree className="w-4 h-4" /> Categories</TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><LayoutTemplate className="w-4 h-4" /> Homepage</TabsTrigger>
            <TabsTrigger value="pages" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><FileStack className="w-4 h-4" /> Pages</TabsTrigger>
            <TabsTrigger value="blog" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><FileText className="w-4 h-4" /> Blog</TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><Settings2 className="w-4 h-4" /> Contact Info</TabsTrigger>
            <TabsTrigger value="chatbot" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><MessageSquare className="w-4 h-4" /> Chatbot</TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-[#C9A227] data-[state=active]:text-white gap-1.5"><Inbox className="w-4 h-4" /> Quotes</TabsTrigger>
          </TabsList>
          <TabsContent value="products"><AdminProducts /></TabsContent>
          <TabsContent value="categories"><AdminCategories /></TabsContent>
          <TabsContent value="content"><AdminContent /></TabsContent>
          <TabsContent value="pages"><AdminPages /></TabsContent>
          <TabsContent value="blog"><AdminBlog /></TabsContent>
          <TabsContent value="settings"><AdminSettings /></TabsContent>
          <TabsContent value="chatbot"><AdminChatbot /></TabsContent>
          <TabsContent value="quotes"><AdminQuotes /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Admin;
